
import { supabase } from "@/integrations/supabase/client";

/**
 * Loads article content from Supabase storage
 * Enhanced to support multiple storage paths and formats
 */
export const loadContentFromSupabase = async (path: string): Promise<string | null> => {
  try {
    console.log(`StorageLoader: Loading content from Supabase storage: ${path}`);
    
    // Try multiple buckets if the first fails
    const buckets = ['articles', 'premium-articles', 'content'];
    
    for (const bucket of buckets) {
      try {
        const { data, error } = await supabase.storage
          .from(bucket)
          .download(path);
          
        if (error) {
          console.log(`StorageLoader: Error loading from bucket ${bucket}: ${error.message}`);
          continue;
        }
        
        if (!data) {
          console.log(`StorageLoader: No data returned from ${bucket}`);
          continue;
        }
        
        // Convert the blob to text
        const content = await data.text();
        console.log(`StorageLoader: Successfully loaded content from bucket ${bucket}, path ${path}, length: ${content.length}`);
        
        return content;
      } catch (bucketError) {
        console.log(`StorageLoader: Error with bucket ${bucket}: ${bucketError}`);
      }
    }
    
    // If path doesn't include an extension, try with extensions
    if (!path.includes('.')) {
      for (const ext of ['.md', '.html', '.txt']) {
        const result = await loadContentFromSupabase(`${path}${ext}`);
        if (result) return result;
      }
    }
    
    // Try the edge function as a last resort
    try {
      console.log(`StorageLoader: Attempting to load via edge function`);
      const slug = path.split('/').pop()?.split('.')[0]; // Extract slug from path
      
      if (slug) {
        const response = await supabase.functions.invoke('get-article-storage', {
          body: { slug }
        });
        
        if (response.data) {
          console.log(`StorageLoader: Successfully loaded via edge function for ${slug}`);
          return response.data;
        }
      }
    } catch (functionError) {
      console.error(`StorageLoader: Edge function error: ${functionError}`);
    }
    
    console.error('StorageLoader: Content not found in any Supabase bucket');
    return null;
  } catch (error) {
    console.error('StorageLoader: Error loading content from Supabase storage:', error);
    return null;
  }
};

/**
 * Saves article content to Supabase storage
 * Useful for admin interfaces or content migration
 */
export const saveContentToSupabase = async (path: string, content: string, contentType = 'text/markdown'): Promise<boolean> => {
  try {
    console.log(`StorageLoader: Saving content to Supabase storage: ${path}`);
    
    const bucket = 'articles'; // Default bucket
    const blob = new Blob([content], { type: contentType });
    
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, blob, {
        contentType,
        upsert: true
      });
      
    if (error) {
      console.error(`StorageLoader: Error saving content: ${error.message}`);
      return false;
    }
    
    console.log(`StorageLoader: Successfully saved content to ${path}`);
    return true;
  } catch (error) {
    console.error('StorageLoader: Error saving content to Supabase storage:', error);
    return false;
  }
};
